import { InfoCircle } from "@untitledui/icons";
import { useState } from "react";
import { toast } from "sonner";
import { useDeleteMerchantLogo, useUploadMerchantLogo } from "@/api/admin/merchant-logo";
import { useUpdateMerchant } from "@/api/admin/update-merchant";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { LogoUploader } from "@/components/application/logo-uploader/logo-uploader";
import { Badge } from "@/components/base/badges/badges";
import { Select } from "@/components/base/select/select";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { TabHeader } from "./tab-header";
import type { MerchantWithUsers } from "./types";

type CompanyDetailsTabProps = {
  merchant: MerchantWithUsers;
};

export const CompanyDetailsTab = ({ merchant }: CompanyDetailsTabProps) => {
  const initialOwnerId = merchant.ownerWorkspace?.id ?? null;
  const [name, setName] = useState(merchant.name);
  const [ownerWorkspaceId, setOwnerWorkspaceId] = useState<string | null>(initialOwnerId);

  const uploadLogo = useUploadMerchantLogo();
  const deleteLogo = useDeleteMerchantLogo();
  const updateMerchant = useUpdateMerchant();

  const { data: workspacesData } = useAdminWorkspaces(
    { offset: 0, limit: 500 },
    { column: "name", direction: "asc" },
    {},
  );
  const workspaceItems = (workspacesData?.workspaces ?? []).map((ws) => ({ id: ws.id, label: ws.name }));

  const hasChanges = name !== merchant.name || ownerWorkspaceId !== initialOwnerId;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleSave = async () => {
    try {
      await updateMerchant.mutateAsync({
        merchantId: merchant.id,
        name,
        features: merchant.features,
        ownerWorkspaceId,
      });
      toast.success("Merchant updated successfully");
    } catch {
      toast.error("Failed to update merchant");
    }
  };

  const handleCancel = () => {
    setName(merchant.name);
    setOwnerWorkspaceId(initialOwnerId);
  };

  const handleUpload = async (file: File) => {
    await uploadLogo.mutateAsync({ merchantId: merchant.id, file });
  };

  const handleDelete = async () => {
    await deleteLogo.mutateAsync(merchant.id);
  };

  return (
    <div className="flex flex-col gap-6">
      <TabHeader
        title="Merchant Details"
        subtitle="These are the details for the merchant"
        hasChanges={hasChanges}
        isPending={updateMerchant.isPending}
        onSave={handleSave}
        onCancel={handleCancel}
      />

      <div className="border-t border-primary" />

      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">
            Name <span className="text-error">*</span>
          </p>
          <p className="text-sm text-tertiary">
            This is how it will look in the navigation to make sure caps and spelling are as desired
          </p>
        </div>
        <div className="flex-1 max-w-md">
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            className="w-full px-3 py-2 text-md text-primary bg-primary rounded-lg ring-1 ring-primary outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-primary">Region</p>
            <Tooltip title="This is auto-pulled through from the TikTok Shop for the merchant region.">
              <TooltipTrigger className="cursor-help text-fg-quaternary">
                <InfoCircle className="w-4 h-4" />
              </TooltipTrigger>
            </Tooltip>
          </div>
          <p className="text-sm text-tertiary">The TikTok Shop region for this merchant, set from its shop</p>
        </div>
        <div className="flex-1 max-w-md flex items-center">
          {merchant.region ? (
            <Badge color="gray-blue">{merchant.region}</Badge>
          ) : (
            <span className="text-sm text-tertiary">No region</span>
          )}
        </div>
      </div>

      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">Owner of Merchant</p>
          <p className="text-sm text-tertiary">The workspace that owns this merchant</p>
        </div>
        <div className="flex-1 max-w-md">
          <Select
            aria-label="Owner of Merchant"
            placeholder="Select owner workspace"
            size="md"
            items={workspaceItems}
            selectedKey={ownerWorkspaceId}
            onSelectionChange={(key) => setOwnerWorkspaceId(key ? String(key) : null)}
            popoverClassName="dark-mode"
          >
            {(workspace) => (
              <Select.Item key={workspace.id} id={workspace.id}>
                {workspace.label}
              </Select.Item>
            )}
          </Select>
        </div>
      </div>

      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">
            Merchant logo <span className="text-error">*</span>
          </p>
          <p className="text-sm text-tertiary">This is the logo that will appear in the navigation for all users</p>
        </div>
        <LogoUploader
          currentLogoUrl={merchant.logoUrl}
          onUpload={handleUpload}
          onDelete={handleDelete}
          isUploading={uploadLogo.isPending}
          isDeleting={deleteLogo.isPending}
        />
      </div>
    </div>
  );
};
